// src/components/RepresentationForm.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "./ui/Select";
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import { useNavigate } from "react-router-dom";
import { createRepresentation } from "../actions/representationActions";
import { fetchCostumers } from "../actions/authActions";
import { UploadInputInterface } from "./ui/UploadInputInterface";
import toast from "react-hot-toast";
import { translateApiMessage } from "../utils/formErrors";
import { toastApiError, toastErrors } from "../utils/toastErrors";
import { REPRESENTATION_LABELS, validateRepresentation } from "../utils/recordValidation";
import {
  BadgeCheck,
  CalendarDays,
  FileUp,
  Hash,
  Loader2,
  Users,
  UserRound,
  X,
  FileText,
} from "lucide-react";

/* ---------- helpers ---------- */
const cx = (...classes) => classes.filter(Boolean).join(" ");

const extractErrorData = (axiosError) =>
  axiosError?.response?.data ?? {
    detail: axiosError?.message || "خطای نامشخص",
  };

const firstErr = (v) => (Array.isArray(v) ? v[0] : v);

/* ---------- Field (same as your previous one) ---------- */
const Field = ({
  id,
  name,
  label,
  placeholder,
  value,
  onChange,
  type = "text",
  required,
  icon: Icon,
  inputMode,
}) => {
  return (
    <div className="space-y-1.5">
      <label
        htmlFor={id}
        className="block text-sm font-medium text-slate-700 text-right"
      >
        {label}
        {required ? <span className="text-rose-500"> *</span> : null}
      </label>

      <div className="relative">
        {Icon ? (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
            <Icon size={18} />
          </span>
        ) : null}

        <input
          id={id}
          name={name}
          type={type}
          inputMode={inputMode}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          required={required}
          className={cx(
            "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-right text-slate-900",
            "placeholder:text-slate-400 shadow-sm",
            "focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary",
            Icon ? "pr-10" : "pr-3"
          )}
        />
      </div>
    </div>
  );
};

/* ---------- SelectField styled to look EXACTLY like Field ---------- */
const SelectField = ({
  id,
  label,
  required,
  icon: Icon,
  isMulti,
  isLoading,
  options,
  value,
  onChange,
  placeholder,
  error,
}) => {
  const selectStyles = useMemo(
    () => ({
      control: (base, state) => ({
        ...base,
        minHeight: "44px",
        borderRadius: "0.75rem",
        borderColor: state.isFocused
          ? "var(--tw-prose-links, #0ea5e9)"
          : "#e2e8f0", // slate-200
        boxShadow: state.isFocused ? "0 0 0 2px rgba(59,130,246,0.15)" : "none",
        backgroundColor: "#ffffff",
        paddingRight: Icon ? "34px" : "6px",
        paddingLeft: "6px",
        direction: "rtl",
        textAlign: "right",
        fontFamily: "Vazirmatn, sans-serif",
      }),
      placeholder: (base) => ({
        ...base,
        color: "#94a3b8", // slate-400
        textAlign: "right",
      }),
      singleValue: (base) => ({
        ...base,
        color: "#0f172a", // slate-900
        textAlign: "right",
      }),
      input: (base) => ({
        ...base,
        textAlign: "right",
      }),
      menu: (base) => ({
        ...base,
        direction: "rtl",
        borderRadius: "0.75rem",
        overflow: "hidden",
        boxShadow:
          "0 10px 15px -3px rgba(0,0,0,0.08), 0 4px 6px -4px rgba(0,0,0,0.06)",
      }),
      option: (base, state) => ({
        ...base,
        textAlign: "right",
        backgroundColor: state.isSelected
          ? "rgba(59,130,246,1)"
          : state.isFocused
          ? "rgba(241,245,249,1)"
          : "#fff",
        color: state.isSelected ? "#fff" : "#0f172a",
      }),
      multiValue: (base) => ({
        ...base,
        backgroundColor: "rgba(59,130,246,0.08)",
        borderRadius: "0.65rem",
      }),
      multiValueLabel: (base) => ({ ...base, color: "#0f172a" }),
      multiValueRemove: (base) => ({
        ...base,
        borderRadius: "0.65rem",
        ":hover": { backgroundColor: "rgba(59,130,246,1)", color: "#fff" },
      }),
    }),
    [Icon]
  );

  return (
    <div className="space-y-1.5">
      <label
        htmlFor={id}
        className="block text-sm font-medium text-slate-700 text-right"
      >
        {label}
        {required ? <span className="text-rose-500"> *</span> : null}
      </label>

      <div className="relative">
        {Icon ? (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 z-10 pointer-events-none">
            <Icon size={18} />
          </span>
        ) : null}

        <Select
          inputId={id}
          name={id}
          isMulti={!!isMulti}
          isLoading={!!isLoading}
          isDisabled={!!isLoading}
          options={options}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          isClearable
          styles={selectStyles}
          menuPortalTarget={document.body}
          menuPosition="fixed"
        />
      </div>

      {error ? (
        <div className="text-rose-700 text-sm mt-1 text-right">{error}</div>
      ) : null}
    </div>
  );
};

const RepresentationForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    costumerList = [],
    customersLoading,
    customersError,
  } = useSelector((s) => s.costumers || {});

  const [submitting, setSubmitting] = useState(false);

  const [serverErrors, setServerErrors] = useState({});

  const [formData, setFormData] = useState({
    representi: [],
    representor: [],
    applicant: "",
    start_date: "",
    end_date: "",
    another_deligation: false,
    representor_dismissal: false,
    representation_summary: "",
    doc_number: "",
    verification_code: "",
    file: null,
  });

  useEffect(() => {
    dispatch(fetchCostumers());
  }, [dispatch]);

  useEffect(() => {
    if (customersError) toast.error("دریافت فهرست مشتریان ناموفق بود.");
  }, [customersError]);

  const customerOptions = useMemo(
    () =>
      (costumerList || []).map((c) => ({
        value: String(c.id),
        label: c.full_name,
      })),
    [costumerList]
  );


  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((p) => ({
      ...p,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e?.target?.files?.[0] || null;
    setFormData((p) => ({ ...p, file }));
  };

  const handleStartDateChange = (val) => {
    setFormData((p) => ({
      ...p,
      start_date: val ? val.format("YYYY-MM-DD") : "",
    }));
  };

  const handleEndDateChange = (val) => {
    setFormData((p) => ({
      ...p,
      end_date: val ? val.format("YYYY-MM-DD") : "",
    }));
  };

  const toMultiValue = (ids) =>
    customerOptions.filter((o) => (ids || []).includes(o.value));

  const toSingleValue = (id) =>
    customerOptions.find((o) => o.value === id) || null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerErrors({});
    const { lines: problems, fields: problemFields } = validateRepresentation(formData);
    if (problems.length) {
      setServerErrors(problemFields);
      toastErrors(problems);
      return;
    }
    setSubmitting(true);

    try {
      const data = new FormData();

      formData.representi.forEach((id) => data.append("representi", id));
      formData.representor.forEach((id) => data.append("representor", id));

      data.append("applicant", formData.applicant || "");
      data.append("start_date", formData.start_date || "");
      data.append("end_date", formData.end_date || "");
      data.append(
        "another_deligation",
        formData.another_deligation ? "true" : "false"
      );
      data.append(
        "representor_dismissal",
        formData.representor_dismissal ? "true" : "false"
      );
      data.append(
        "representation_summary",
        formData.representation_summary || ""
      );
      data.append("doc_number", String(formData.doc_number ?? ""));
      data.append(
        "verification_code",
        String(formData.verification_code ?? "")
      );

      if (formData.file instanceof File) {
        data.append("file", formData.file);
      }

      await dispatch(createRepresentation(data));
      toast.success("وکالت‌نامه با موفقیت ثبت شد.");
      navigate("/representations");
    } catch (err) {
      setServerErrors(extractErrorData(err));
      toastApiError(err, REPRESENTATION_LABELS, "ثبت وکالت‌نامه ناموفق بود.");
    } finally {
      setSubmitting(false);
    }
  };

  const fieldError = (key) =>
    serverErrors?.[key] ? translateApiMessage(firstErr(serverErrors[key])) : "";

  return (
    <div className="mt-10 sm:mt-16 md:mt-20 lg:mt-24 xl:mt-28 px-3 sm:px-6 font-vazirmatn">
      <form onSubmit={handleSubmit} noValidate dir="rtl" className="max-w-3xl mx-auto">
        <div className="bg-white/95 backdrop-blur rounded-2xl shadow-lg border border-slate-200/80 overflow-hidden">
          {/* Header (same structure) */}
          <div className="px-5 sm:px-7 py-5 border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
            <div className="flex flex-row-reverse items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => navigate("/representations")}
                className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 transition"
              >
                <X size={18} />
                بازگشت
              </button>

              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
                  <BadgeCheck size={22} />
                </div>
                <div className="text-right">
                  <h2 className="text-lg text-right sm:text-xl font-extrabold text-slate-800">
                    افزودن وکالت‌نامه
                  </h2>
                  <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
                    اطلاعات وکالت‌نامه را وارد کنید و در صورت نیاز فایل را پیوست
                    کنید.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="px-5 sm:px-7 py-6">
            {customersError ? (
              <div className="mb-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-right text-rose-700 text-sm">
                خطا در دریافت لیست مشتریان
              </div>
            ) : null}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* موکل */}
              <SelectField
                id="representi"
                label="موکل"
                required
                icon={Users}
                isMulti
                isLoading={customersLoading}
                options={customerOptions}
                value={toMultiValue(formData.representi)}
                onChange={(opts) =>
                  setFormData((p) => ({
                    ...p,
                    representi: (opts || []).map((o) => o.value),
                  }))
                }
                placeholder="انتخاب موکل"
                error={fieldError("representi")}
              />

              {/* وکیل */}
              <SelectField
                id="representor"
                label="وکیل"
                required
                icon={Users}
                isMulti
                isLoading={customersLoading}
                options={customerOptions}
                value={toMultiValue(formData.representor)}
                onChange={(opts) =>
                  setFormData((p) => ({
                    ...p,
                    representor: (opts || []).map((o) => o.value),
                  }))
                }
                placeholder="انتخاب وکیل"
                error={fieldError("representor")}
              />

              {/* درخواست دهنده */}
              <SelectField
                id="applicant"
                label="درخواست دهنده"
                icon={UserRound}
                isLoading={customersLoading}
                options={customerOptions}
                value={toSingleValue(formData.applicant)}
                onChange={(opt) =>
                  setFormData((p) => ({
                    ...p,
                    applicant: opt ? opt.value : "",
                  }))
                }
                placeholder="انتخاب درخواست دهنده"
                error={fieldError("applicant")}
              />

              {/* شماره سند */}
              <Field
                id="doc_number"
                name="doc_number"
                label="شماره سند"
                placeholder="مثلاً 12345"
                value={formData.doc_number}
                onChange={handleChange}
                required
                icon={Hash}
                inputMode="numeric"
              />

              {/* کد تصدیق */}
              <Field
                id="verification_code"
                name="verification_code"
                label="کد تصدیق"
                placeholder="مثلاً 98765"
                value={formData.verification_code}
                onChange={handleChange}
                required
                icon={Hash}
                inputMode="numeric"
              />

              {/* تاریخ شروع */}
              <div className="space-y-1.5">
                <label
                  htmlFor="start_date"
                  className="block text-sm font-medium text-slate-700 text-right"
                >
                  تاریخ شروع <span className="text-rose-500">*</span>
                </label>

                <div className="relative">
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                    <CalendarDays size={18} />
                  </span>

                  <DatePicker
                    id="start_date"
                    name="start_date"
                    calendar={persian}
                    locale={persian_fa}
                    value={formData.start_date}
                    onChange={handleStartDateChange}
                    format="YYYY-MM-DD"
                    inputClass={cx(
                      "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 pr-10 text-right text-slate-900",
                      "placeholder:text-slate-400 shadow-sm",
                      "focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary"
                    )}
                    containerClassName="w-full"
                    calendarPosition="bottom-right"
                  />
                </div>
                {fieldError("start_date") ? (
                  <div className="text-rose-700 text-sm mt-1 text-right">
                    {fieldError("start_date")}
                  </div>
                ) : null}
              </div>

              {/* تاریخ پایان */}
              <div className="space-y-1.5">
                <label
                  htmlFor="end_date"
                  className="block text-sm font-medium text-slate-700 text-right"
                >
                  تاریخ پایان
                </label>

                <div className="relative">
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                    <CalendarDays size={18} />
                  </span>

                  <DatePicker
                    id="end_date"
                    name="end_date"
                    calendar={persian}
                    locale={persian_fa}
                    value={formData.end_date}
                    onChange={handleEndDateChange}
                    format="YYYY-MM-DD"
                    inputClass={cx(
                      "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 pr-10 text-right text-slate-900",
                      "placeholder:text-slate-400 shadow-sm",
                      "focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary"
                    )}
                    containerClassName="w-full"
                    calendarPosition="bottom-right"
                  />
                </div>
                {fieldError("end_date") ? (
                  <div className="text-rose-700 text-sm mt-1 text-right">
                    {fieldError("end_date")}
                  </div>
                ) : null}
              </div>

              {/* خلاصه وکالت */}
              <div className="md:col-span-2 space-y-1.5">
                <label className="block text-sm font-medium text-slate-700 text-right">
                  خلاصه وکالت <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute right-3 top-3 text-slate-400">
                    <FileText size={18} />
                  </span>
                  <textarea
                    name="representation_summary"
                    value={formData.representation_summary}
                    onChange={handleChange}
                    required
                    placeholder="خلاصه‌ای از وکالت را وارد کنید"
                    className={cx(
                      "w-full min-h-[120px] rounded-xl border border-slate-200 bg-white px-3 py-2.5 pr-10 text-right text-slate-900",
                      "placeholder:text-slate-400 shadow-sm resize-y",
                      "focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary"
                    )}
                  />
                </div>
                {fieldError("representation_summary") ? (
                  <div className="text-rose-700 text-sm mt-1 text-right">
                    {fieldError("representation_summary")}
                  </div>
                ) : null}
              </div>
            </div>

            {/* Switches + file (same “two cards” section as before) */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* another_deligation */}
              <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-right">
                    <div className="text-sm font-semibold text-slate-800">
                      توکل به غیر
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      در صورت امکان توکیل به غیر، فعال کنید.
                    </div>
                  </div>

                  <label className="relative inline-flex items-center cursor-pointer select-none">
                    <input
                      type="checkbox"
                      id="another_deligation"
                      name="another_deligation"
                      checked={!!formData.another_deligation}
                      onChange={handleChange}
                      className="sr-only peer"
                    />
                    <span
                      className="
                        relative w-11 h-6 rounded-full bg-slate-200 transition-colors
                        peer-checked:bg-primary
                        after:content-[''] after:absolute after:top-0.5 after:right-0.5 after:h-5 after:w-5
                        after:rounded-full after:bg-white after:shadow after:transition-transform
                        peer-checked:after:-translate-x-5
                      "
                    />
                    <span className="mr-3 text-sm font-medium text-slate-700">
                      فعال
                    </span>
                  </label>
                </div>
              </div>

              {/* representor_dismissal */}
              <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-right">
                    <div className="text-sm font-semibold text-slate-800">
                      عزل وکیل
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      در صورت امکان عزل، فعال کنید.
                    </div>
                  </div>

                  <label className="relative inline-flex items-center cursor-pointer select-none">
                    <input
                      type="checkbox"
                      id="representor_dismissal"
                      name="representor_dismissal"
                      checked={!!formData.representor_dismissal}
                      onChange={handleChange}
                      className="sr-only peer"
                    />
                    <span
                      className="
                        relative w-11 h-6 rounded-full bg-slate-200 transition-colors
                        peer-checked:bg-primary
                        after:content-[''] after:absolute after:top-0.5 after:right-0.5 after:h-5 after:w-5
                        after:rounded-full after:bg-white after:shadow after:transition-transform
                        peer-checked:after:-translate-x-5
                      "
                    />
                    <span className="mr-3 text-sm font-medium text-slate-700">
                      فعال
                    </span>
                  </label>
                </div>
              </div>

              {/* File */}
              <div className="md:col-span-2 rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="text-right">
                    <div className="text-sm font-semibold text-slate-800">
                      فایل پیوست
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      تصویر یا PDF وکالت‌نامه (اختیاری)
                    </div>
                  </div>
                  <div className="text-slate-400 mt-0.5">
                    <FileUp size={18} />
                  </div>
                </div>

                <div className="mt-3">
                  <UploadInputInterface
                    file={formData.file}
                    onFileChange={handleFileChange}
                  />
                </div>

                {fieldError("file") ? (
                  <div className="text-rose-700 text-sm mt-1 text-right">
                    {fieldError("file")}
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {/* Footer (same structure) */}
          <div className="px-5 sm:px-7 py-4 border-t border-slate-200 bg-white">
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <button
                type="button"
                onClick={() => {
                  setServerErrors({});
                  setFormData({
                    representi: [],
                    representor: [],
                    applicant: "",
                    start_date: "",
                    end_date: "",
                    another_deligation: false,
                    representor_dismissal: false,
                    representation_summary: "",
                    doc_number: "",
                    verification_code: "",
                    file: null,
                  });
                }}
                className="inline-flex items-center justify-center rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition"
                disabled={submitting}
              >
                پاک کردن فرم
              </button>

              <button
                type="submit"
                disabled={submitting}
                className={cx(
                  "inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-bold text-white",
                  "bg-primary hover:opacity-95 active:opacity-90 transition shadow-sm",
                  "disabled:opacity-50 disabled:cursor-not-allowed"
                )}
              >
                {submitting ? (
                  <>
                    <Loader2 className="animate-spin" size={18} />
                    در حال ثبت...
                  </>
                ) : (
                  "ثبت"
                )}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default RepresentationForm;
